/**
 * Roster section nav — group memberships (roster UX audit 2026-06-10 §5).
 *
 * The roster IA has one shell-owned navigation control:
 *   Group selector — the five-group SubPageTabs from the `/roster` entry in
 *           `@/lib/navigation/sub-page-tabs-registry` (Schedule / Cover & attendance /
 *           Patterns & periods / Leave & availability / Time & closeout).
 *   Active destinations — this file's segments, rendered once in the shell
 *           by `RosterSectionNavClient`. Legacy page placements are inert.
 *
 * Both rows are sourced from `roster-navigation-ia.ts`; this module only
 * resolves the active row for rendering.
 *
 * Pure data + pure resolution helpers only — imported by both server and
 * client components (no 'use client' / 'server-only' directives here).
 */

import type { SubPageTab } from '@statura/ui';
import { resolveActiveTab } from '@statura/ui';
import { resolveSubPageTabs } from '@/lib/navigation/sub-page-tabs-registry';
import {
  ROSTER_SECTION_SEGMENTS,
  rosterNavigationPath,
  type RosterPrimaryGroupId,
  type RosterSectionSegment,
} from '@/lib/roster/roster-navigation-ia';

export { ROSTER_SECTION_SEGMENTS };
export type { RosterSectionSegment };

export interface ResolvedRosterSectionGroup {
  groupId: string;
  groupLabel: string;
  segments: ReadonlyArray<RosterSectionSegment>;
  /** Href of the active segment (longest-prefix winner), or null. */
  activeSegmentHref: string | null;
}

/**
 * Resolve the active roster group + its segments for a pathname.
 * Returns null off the roster section or when the path belongs to no
 * group (shouldn't happen — every group claims its routes via activeFor).
 */
export function resolveRosterSectionGroup(pathname: string): ResolvedRosterSectionGroup | null {
  const activePath = rosterNavigationPath(pathname);
  const resolved = resolveSubPageTabs(activePath);
  if (!resolved || resolved.parentHref !== '/roster') return null;
  const activeGroupHref = resolveActiveTab(resolved.tabs, activePath);
  if (!activeGroupHref) return null;
  const groupTab = resolved.tabs.find((tab: SubPageTab) => tab.href === activeGroupHref);
  if (!groupTab) return null;
  if (!(groupTab.id in ROSTER_SECTION_SEGMENTS)) return null;
  const segments = ROSTER_SECTION_SEGMENTS[groupTab.id as RosterPrimaryGroupId];
  if (!segments || segments.length === 0) return null;
  // Reuse the tab resolver for the active segment — same longest-prefix rule.
  const activeSegmentHref = resolveActiveTab(
    segments.map(({ id, label, href }) => ({
      id,
      label,
      href,
      activeFor: [rosterNavigationPath(href)],
    })),
    activePath,
  );
  return {
    groupId: groupTab.id,
    groupLabel: groupTab.label,
    segments,
    activeSegmentHref,
  };
}
